/**
 * ListPageHeader - standard header for list pages.
 *
 * Title (Cormorant Garamond 48px)
 * Subtitle: {count} total · {highlight stat} · <TimeFilter />
 * Optional action button on the right.
 */

import { TimeFilter, type TimeRange } from './time-filter'
import { Button } from '@/shared/components/ui/button'
import { ConfigHelp, type ConfigSectionKey } from '@/admin/components/config-help'

interface HighlightStat {
  label: string
  value: string | number
}

interface ListPageHeaderProps {
  title: string
  totalCount: number
  highlightStat?: HighlightStat
  timeFilterValue: TimeRange
  timeFilterCustomRange?: { from: string; to: string }
  onTimeFilterChange: (range: TimeRange, custom?: { from: string; to: string }) => void
  actionButton?: {
    label: string
    onClick: () => void
    dataTour?: string
  }
  /** Optional secondary (outline) action shown to the left of the primary. */
  secondaryActionButton?: {
    label: string
    onClick: () => void
    dataTour?: string
  }
  /** Optional config-help affordance shown next to the title (staff/admins). */
  configHelp?: {
    sections: ConfigSectionKey[]
    returnLabel: string
  }
}

export type { ListPageHeaderProps }

export function ListPageHeader({
  title,
  totalCount,
  highlightStat,
  timeFilterValue,
  timeFilterCustomRange,
  onTimeFilterChange,
  actionButton,
  secondaryActionButton,
  configHelp,
}: ListPageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="clara-title">{title}</h1>
          {configHelp && (
            <ConfigHelp
              sections={configHelp.sections}
              returnLabel={configHelp.returnLabel}
            />
          )}
        </div>
        <div className="mt-1 flex items-center flex-wrap text-[12px] text-[var(--ink-3)]">
          {totalCount} total
          {highlightStat && (
            <>
              <span className="mx-1.5 opacity-35">&middot;</span>
              {highlightStat.value} {highlightStat.label}
            </>
          )}
          <span className="mx-1.5 opacity-35">&middot;</span>
          <TimeFilter
            value={timeFilterValue}
            customRange={timeFilterCustomRange}
            onChange={onTimeFilterChange}
          />
        </div>
      </div>
      {(actionButton || secondaryActionButton) && (
        <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto sm:mb-1">
          {secondaryActionButton && (
            <Button
              variant="outline"
              onClick={secondaryActionButton.onClick}
              data-tour={secondaryActionButton.dataTour}
              className="cursor-pointer rounded-[4px] px-4 py-2.5 text-[11px] font-normal uppercase tracking-[0.1em]"
            >
              {secondaryActionButton.label}
            </Button>
          )}
          {actionButton && (
            <Button
              onClick={actionButton.onClick}
              data-tour={actionButton.dataTour}
              className="text-[11px] font-normal uppercase tracking-[0.1em] rounded-[4px] px-4 py-2.5 cursor-pointer transition-colors duration-150 bg-[var(--green)] hover:bg-[var(--green-hover)] text-white border-none"
            >
              + {actionButton.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
